(() => {
  const SELECTOR = '[data-ohso-landing]';

  class OHSoLanding {
    constructor(section) {
      this.section = section;
      this.modal = section.querySelector('[data-ohso-modal]');
      this.dialog = section.querySelector('[data-ohso-modal-dialog]');
      this.openButton = section.querySelector('[data-ohso-modal-open]');
      this.closeButtons = section.querySelectorAll('[data-ohso-modal-close]');
      this.form = section.querySelector('.ohso-form');
      this.formState = section.querySelector('[data-ohso-form-state]');
      this.successState = section.querySelector('[data-ohso-success-state]');
      this.emailInput = section.querySelector('[data-ohso-email]');
      this.submitButton = section.querySelector('[data-ohso-submit]');
      this.message = section.querySelector('[data-ohso-form-message]');
      this.previouslyFocused = null;
      this.isSubmitting = false;

      if (!this.modal || !this.dialog || !this.openButton || !this.form) {
        return;
      }

      this.handleOpen = this.open.bind(this);
      this.handleClose = this.close.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);

      this.bindEvents();
      this.openExistingResult();
    }

    bindEvents() {
      this.openButton.addEventListener('click', this.handleOpen);

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.handleClose);
      });

      this.form.addEventListener('submit', this.handleSubmit);
    }

    open() {
      this.previouslyFocused = document.activeElement;

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ohso-modal-open');
      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        const target = this.successIsVisible()
          ? this.dialog
          : this.emailInput;

        target?.focus({ preventScroll: true });
      });
    }

    close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ohso-modal-open');
      document.removeEventListener('keydown', this.handleKeydown);

      if (
        this.previouslyFocused &&
        typeof this.previouslyFocused.focus === 'function'
      ) {
        this.previouslyFocused.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.dialog.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
      ).filter((element) => {
        return !element.hidden && element.offsetParent !== null;
      });

      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async onSubmit(event) {
      if (this.isSubmitting) {
        event.preventDefault();
        return;
      }

      if (!this.form.checkValidity()) {
        return;
      }

      event.preventDefault();

      this.setLoading(true);
      this.clearMessage();

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(`Shopify returned status ${response.status}`);
        }

        const html = await response.text();
        const result = this.parseResponse(html);

        if (result.success) {
          this.showSuccess();
          return;
        }

        if (result.error) {
          this.showMessage(result.error);
          return;
        }

        /*
         * Shopify can occasionally return a challenge or an unexpected
         * response. Falling back to a standard submission preserves the
         * native Shopify behavior instead of losing the signup.
         */
        this.form.removeEventListener('submit', this.handleSubmit);
        this.form.submit();
      } catch (error) {
        console.error('OH SO signup form error:', error);

        this.form.removeEventListener('submit', this.handleSubmit);
        this.form.submit();
      } finally {
        this.setLoading(false);
      }
    }

    parseResponse(html) {
      const parser = new DOMParser();
      const documentResponse = parser.parseFromString(html, 'text/html');
      const sectionId = this.section.dataset.sectionId;

      const returnedSection = documentResponse.querySelector(
        `[data-ohso-landing][data-section-id="${CSS.escape(sectionId)}"]`
      );

      if (!returnedSection) {
        return {
          success: false,
          error: null
        };
      }

      const returnedSuccess = returnedSection.querySelector(
        '[data-ohso-form-success="true"]'
      );

      if (returnedSuccess) {
        return {
          success: true,
          error: null
        };
      }

      const returnedMessage = returnedSection.querySelector(
        '[data-ohso-form-message]'
      );

      const errorText = returnedMessage?.textContent
        ?.replace(/\s+/g, ' ')
        .trim();

      return {
        success: false,
        error: errorText || null
      };
    }

    showSuccess() {
      this.formState.hidden = true;
      this.successState.hidden = false;
      this.successState.dataset.ohsoFormSuccess = 'true';
      this.form.reset();

      const closeButton = this.successState.querySelector(
        '[data-ohso-modal-close]'
      );

      closeButton?.focus({ preventScroll: true });
    }

    successIsVisible() {
      return (
        this.successState &&
        !this.successState.hidden &&
        this.successState.dataset.ohsoFormSuccess === 'true'
      );
    }

    showMessage(message) {
      if (!this.message) {
        return;
      }

      this.message.textContent =
        message || 'Something went wrong. Please try again.';
      this.message.hidden = false;
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailInput?.focus();
    }

    clearMessage() {
      if (!this.message) {
        return;
      }

      this.message.textContent = '';
      this.message.hidden = true;
      this.emailInput?.removeAttribute('aria-invalid');
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.form.classList.toggle('is-loading', isLoading);

      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute(
          'aria-busy',
          isLoading ? 'true' : 'false'
        );
      }
    }

    openExistingResult() {
      const hasSuccess = this.successIsVisible();
      const hasError =
        this.message &&
        !this.message.hidden &&
        this.message.textContent.trim().length > 0;

      if (hasSuccess || hasError) {
        this.open();
      }
    }

    destroy() {
      this.openButton.removeEventListener('click', this.handleOpen);

      this.closeButtons.forEach((button) => {
        button.removeEventListener('click', this.handleClose);
      });

      this.form.removeEventListener('submit', this.handleSubmit);
      document.removeEventListener('keydown', this.handleKeydown);
    }
  }

  const instances = new WeakMap();

  function initialize(root = document) {
    root.querySelectorAll(SELECTOR).forEach((section) => {
      if (instances.has(section)) {
        return;
      }

      instances.set(section, new OHSoLanding(section));
    });
  }

  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SELECTOR);

    if (!section) {
      return;
    }

    const instance = instances.get(section);
    instance?.destroy();
    instances.delete(section);
  });
})();